import { Vue, Component, Watch } from 'vue-property-decorator';
import { IReportSchedule, ISchedule } from '@/Interfaces';
import { LSReportSource, LSDeliveryOptionSource } from '@/Sources';
import { SchedulerInput } from '@/Components/SchedulerInput';
import { VuePlugin } from 'vuera';
import { Btn, Field, DropdownField, TextField, RangeField, NumberField, Checkbox, FormButtonDisabler, Toolbar } from '@samc/centricity-core';
import { DeliveryOption, ISchedulerConfig, FileType, AppendTimeStamp } from '@samc/querybuilder';
import { ReportScheduleValidator } from './Validators';
import { zonedTimeToUtc } from 'date-fns-tz';

import '@/registerVuePlugins';
import { AjaxHelper, errToMessage } from '@samc/common';

interface KeyTextPair<T> {
  key: T;
  text: T;
}

Vue.use(VuePlugin);

@Component({
  name: 'ReportingScheduledExportsEdit',
  components: { Btn, Field, DropdownField, TextField, RangeField, NumberField, Checkbox, FormButtonDisabler, Toolbar, SchedulerInput }
})
class EditComponent extends Vue {
  public model: IReportSchedule = {
    exportName: '',
    reportGuid: '',
    deliveryLocation: '',
    appendTimestamp: 'Date',
    fileType: 'Excel',
    fileName: '',
    isActive: true,

    /// Not used fields
    id: -1,
    frequency: '',
    status: undefined,
    errorMessage: '',
    schedule: {
      id: -1,
      startDate: new Date().toISOString(),
      endDate: null,
      nextFireTime: null,
      nextFireTimeDisplay: null,
      type: 'hourly',
      hourly: 1,
      daily: 'each',
      weekly: [],
      monthly: 1,
      monthType: 'day',
      monthOnDay: 1,
      monthWeekDay: 'monday',
      startTime: new Date(0, 0, 0, 0, 0, 0, 0).toISOString(),
      cron: ''
    }
  };

  public fileTypes: KeyTextPair<FileType | 'CSV'>[] = [{ key: 'Excel', text: 'Excel' }, { key: 'Text', text: 'Text' }, { key: 'CSV', text: 'CSV' }];
  public timestampTypes: KeyTextPair<AppendTimeStamp>[] = [{ key: 'None', text: 'None' }, { key: 'Date', text: 'Date' }, { key: 'DateTime', text: 'DateTime' }];
  public deliveryLocations: LSDeliveryOptionSource | DeliveryOption[] = [];
  public reports: LSReportSource | any[] = [];
  public verificationToken: string = '';
  public isNew: boolean = true;
  public loaded: boolean = false;

  public validations: Record<string, string> | null = null;

  public get isValid() { return this.validations === null || Object.keys(this.validations).length === 0 }

  public get timeZone() { return window.__UI_TIMEZONE; }

  public async mounted() {
    this.verificationToken = await this.$verificationToken();

    if (this.$refs['mainView'] !== undefined) {
      const mainView = this.$refs['mainView'] as HTMLDivElement;
      const id = mainView.dataset?.id;
      if (id !== undefined && id.length > 0) {
        try {
          this.$loading('Loading Export');
          const helper = new AjaxHelper('/componentapi/reportschedule', this.verificationToken, null, null);
          const model = await helper.getData<IReportSchedule>(id);

          this.model = model;
          this.isNew = false;
        } finally {
          this.$loading();
        }
      }
    }

    this.reports = new LSReportSource();
    this.deliveryLocations = new LSDeliveryOptionSource();

    /// Solution to handle sending data to the database that goes below the range of the
    /// DB so we get current date an zero out minutes and hours since that is when we should start
    if (this.isNew && this.model.schedule !== undefined) {
      const start = new Date();

      start.setHours(0);
      start.setMinutes(0);
      start.setSeconds(0);
      start.setMilliseconds(0);

      const utcStart = zonedTimeToUtc(start, window.__UI_TIMEZONE).toISOString();

      this.model.schedule.startDate = utcStart;
      this.model.schedule.startTime = utcStart;
    }

    this.loaded = true;
  }

  public onSchedulerChange(config: ISchedulerConfig) {
    if (this.model.schedule !== undefined) {
      this.model.schedule = { ...this.model.schedule, ...config };
    }
  }

  public getValidationErrorText(key: string) {
    return this.validations === null ? '' : (this.validations[key] ?? '');
  }

  public async remove() {
    const result = await this.$confirm({
      type: 'warning',
      title: 'Delete Report',
      hasOverlay: true,
      description: 'Are you sure you want to delete this report?',
      errors: ['This cannot be undone.'],
      button: {
        text: 'Delete',
        data: true
      },
      link: {
        text: 'Cancel',
        data: false
      }
    });

    if (!result)
      return;

    try {
      this.$loading('Removing Scheduled Report');
      const helper = new AjaxHelper(`/componentapi/reportschedule/${this.model.id}`, this.verificationToken, null, null);
      const result = await helper.delete<boolean>('');
      if (result)
        window.location.href = '/Reporting/ScheduledExports';
      else
        this.$alert.error('Failed To Remove Scheduled Report');
    } catch (err: any) {
      this.$log('Schedule report delete error occured', err);
      this.$alert({
        type: 'error',
        title: `Error occured`,
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
    } finally {
      this.$loading();
    }
  }

  public async save() {
    try {
      var verb = this.isNew ? 'Adding' : 'Updating';
      this.$loading(`${verb} Scheduled Export`);

      var validator = new ReportScheduleValidator();
      this.validations = validator.isValid(this.model);

      if (Object.keys(this.validations).length === 0) {
        this.validations = null;
        const helper = new AjaxHelper('/componentapi/reportschedule', this.verificationToken, null, null);

        if (this.isNew) {
          this.model = await helper.postData<IReportSchedule>('', this.model);
          this.isNew = false;

          this.$alert.success('Successfully Added Schedule Export');
        } else {
          await helper.putData('', this.model);

          this.$alert.success('Successfully Updated Schedule Export');
        }
      }
    } catch (err: any) {
      this.$log('Report Schedule error occured', err);
      this.$alert({
        type: 'error',
        title: `Error occured`,
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
    } finally {
      this.$loading();
    }
  }

  @Watch('model', { deep: true })
  public onModelChange() {
    if (this.validations !== null) {
      var validator = new ReportScheduleValidator();
      this.validations = validator.isValid(this.model);
    }
  }
}

new EditComponent()
  .$mount('#scheduled-exports-edit-view');